import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Tooltip,
    Select,
    SelectItem,
    Input,
    Textarea,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Chip,
} from "@heroui/react";
import { FileLog, FileLogStatus } from "shared/types/file";
import { ArrowUpRight, Eye, FloppyDisk, Trash, User } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { AlertCircle, Upload } from "assets/icons";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";
import { useDisclosure } from "@heroui/react";
import { toast } from "react-toastify";
import SearchComponent, { SearchMode } from "../searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";
import { Link } from "react-router-dom";


interface FileLogTableProps {
    acceptedLabels: any[];
    onUploadClick: () => void;
    building?: BuildingDTO;
    unit?: UnitDTO;
    wordplay?: any;
}

interface EditedFileLog {
    id: string;
    status: FileLogStatus;
    summary: string;
    statusDescription: string;
    documentDate?: string;
    expirationDate?: string;
    relations?: Array<{
        id: number;
        resourceId: string;
        type: ResourceType;
        name: string;
    }>;
}

const FileLogTable = forwardRef(({
    acceptedLabels,
    onUploadClick,
    building,
    unit,
}: FileLogTableProps, ref) => {
    const [fileLogs, setFileLogs] = useState<FileLog[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editedLogs, setEditedLogs] = useState<{ [key: string]: EditedFileLog }>({});
    const [editMode, setEditMode] = useState<{ [key: string]: boolean }>({});
    const { session } = useSession();
    const [fileToDelete, setFileToDelete] = useState<string | null>(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const fetchFileLogs = async () => {
        setIsLoading(true);
        try {
            const queryParams = new URLSearchParams();

            if (building?.id) {
                queryParams.append('buildingId', building.id);
            }
            if (unit?.id) {
                queryParams.append('unitId', unit.id);
            }
            if (acceptedLabels.length > 0) {
                queryParams.append('labels', acceptedLabels.join(','));
            }

            const response = await fetchWithAuth(
                `${API_URL}/file/get-file-logs?${queryParams.toString()}`,
                {
                    method: 'GET',
                },
                session
            );

            const data = await response.json();
            setFileLogs(data);
            console.log(data);
        } catch (error) {
            console.error('Error fetching file logs:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchFileLogs();
    }, [building?.id, unit?.id, acceptedLabels.join(',')]);

    const hasUnsavedChanges = Object.keys(editedLogs).length > 0;

    const handleSummaryChange = (id: string, newSummary: string) => {
        setEditedLogs(prev => ({
            ...prev,
            [id]: { ...prev[id], summary: newSummary }
        }));
    };

    const handleStatusChange = (id: string, e: React.ChangeEvent<HTMLSelectElement>) => {
        const newStatus = e.target.value as FileLogStatus;
        const fileLog = fileLogs.find((log: any) => log.id === id);
        if (!fileLog) return;

        setEditedLogs(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                id,
                status: newStatus,
                statusDescription: prev[id]?.statusDescription ?? fileLog.statusDescription ?? '',
            },
        }));
    };

    const handleDescriptionChange = (id: string, newDescription: string) => {
        const fileLog = fileLogs.find((log: any) => log.id === id);
        if (!fileLog) return;

        setEditedLogs(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                id,
                status: prev[id]?.status ?? fileLog.status,
                statusDescription: newDescription,
            },
        }));
    };

    const handleDateChange = (id: string, field: 'documentDate' | 'expirationDate', value: string) => {
        const fileLog = fileLogs.find((log: any) => log.id === id);
        if (!fileLog) return;

        setEditedLogs(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                id,
                status: prev[id]?.status ?? fileLog.status,
                statusDescription: prev[id]?.statusDescription ?? fileLog.statusDescription ?? '',
                [field]: value
            },
        }));
    };

    const saveChanges = async () => {
        try {
            const updates = Object.values(editedLogs).map(update => {
                const changedFields: Partial<EditedFileLog> = {};

                if (update.status !== undefined) changedFields.status = update.status;
                if (update.statusDescription !== undefined) changedFields.statusDescription = update.statusDescription;
                if (update.documentDate !== undefined) changedFields.documentDate = update.documentDate;
                if (update.expirationDate !== undefined) changedFields.expirationDate = update.expirationDate;
                if (update.summary !== undefined) changedFields.summary = update.summary;
                if (update.relations !== undefined) changedFields.relations = update.relations;

                return fetchWithAuth(
                    `${API_URL}/file/update-file-log/${update.id}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(changedFields),
                    },
                    session
                );
            });

            await Promise.all(updates);
            await fetchFileLogs();
            setEditedLogs({});
            setEditMode({});
        } catch (error) {
            console.error('Error saving changes:', error);
        }
    };

    const toggleEditMode = (id: string) => {
        setEditMode(prev => {
            const newEditMode = {
                ...prev,
                [id]: !prev[id]
            };

            if (newEditMode[id]) {
                const fileLog = fileLogs.find((log: any) => log.id === id);
                if (fileLog) {
                    setEditedLogs((prev: any) => ({
                        ...prev,
                        [id]: {
                            ...prev[id],
                            id: id,
                            relations: fileLog.relations || []
                        }
                    }));
                }
            }

            return newEditMode;
        });
    };

    const statusOptions = [
        { value: FileLogStatus.OK, label: "Conform", color: "bg-green-500" },
        { value: FileLogStatus.NOT_REQUIRED, label: "Niet van toepassing", color: "bg-green-500" },
        { value: FileLogStatus.OK_WITH_REMARKS, label: "Conform met opmerkingen", color: "bg-orange-500" },
        { value: FileLogStatus.NOT_OK, label: "Niet conform", color: "bg-red-500" },
        { value: FileLogStatus.UNKNOWN, label: "Onbekend", color: "bg-transparent" },
    ];

    const handleDelete = async () => {
        console.log(fileToDelete);
        if (!fileToDelete) return;

        try {
            let res = await fetchWithAuth(
                `${API_URL}/file`,
                {
                    method: 'DELETE',
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(fileToDelete),
                },
                session
            );
            if (res.status == 200) {

                await fetchFileLogs();
                toast.success("Bestand werd verwijderd!");
            }

        } catch (error) {
            console.error('Error deleting file log:', error);
        } finally {
            setFileToDelete(null);
            onClose();
        }
    };

    const sortedFileLogs = [...fileLogs].sort((a, b) => {
        // For community files (building level), put them at the top
        if (!a.unit && !b.unit) return 0;
        if (!a.unit) return -1;
        if (!b.unit) return 1;

        // Sort by unit ID for unit-level files
        return (a.unit.id || '').localeCompare(b.unit.id || '');
    });

    const handleRelationSelect = (logId: string, relation: any) => {
        setEditedLogs(prev => {
            const currentLog = prev[logId] || {};
            const currentRelations = currentLog.relations || [];
            const isAlreadySelected = currentRelations.some(r => r.resourceId === relation.resourceId);

            if (isAlreadySelected) return prev;

            return {
                ...prev,
                [logId]: {
                    ...currentLog,
                    id: logId,
                    relations: [...currentRelations, relation]
                }
            };
        });
    };

    const handleRemoveRelation = (logId: string, relationId: string) => {
        setEditedLogs(prev => ({
            ...prev,
            [logId]: {
                ...prev[logId],
                id: logId,
                relations: prev[logId]?.relations?.filter(r => r.resourceId !== relationId) || []
            }
        }));
    };

    // Expose the save function and unsaved changes status through the ref
    useImperativeHandle(ref, () => ({
        saveChanges: async () => {
            await saveChanges();
        },
        hasUnsavedChanges
    }));

    return (
        <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    {hasUnsavedChanges && (
                        <>
                            <span className="text-orange-500">
                                Opgelet, je hebt onopgeslagen wijzigingen
                            </span>
                            <ButtonNew
                                onClick={saveChanges}
                                icon={<FloppyDisk />}
                                variant="primaryMoss"
                            />
                        </>
                    )}
                </div>
                <ButtonNew
                    onClick={onUploadClick}
                    icon={<Upload />}
                    variant="primaryMoss"
                />
            </div>

            <div className="overflow-auto relative border border-neutral-200 rounded-lg max-h-[60vh]">
                <Table
                    aria-label="File logs table"
                    className="min-w-full border-collapse"
                    removeWrapper
                >
                    <TableHeader className="sticky top-0 z-20">
                        <TableColumn className="sticky left-0 z-30 py-3 px-4 bg-neutral-100 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider border-b border-r border-neutral-200">Unit</TableColumn>
                        <TableColumn className="py-3 px-4 bg-neutral-100 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider border-b border-neutral-200">Label</TableColumn>
                        <TableColumn className="py-3 px-4 bg-neutral-100 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider min-w-[180px] border-b border-neutral-200">Conformiteit</TableColumn>
                        <TableColumn className="py-3 px-4 bg-neutral-100 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider min-w-[250px] border-b border-neutral-200">Opmerkingen op de conformiteit</TableColumn>
                        <TableColumn className="py-3 px-4 bg-neutral-100 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider min-w-[250px] border-b border-neutral-200">Samenvatting</TableColumn>
                        <TableColumn className="py-3 px-4 bg-neutral-100 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider min-w-[120px] border-b border-neutral-200">Documentdatum</TableColumn>
                        <TableColumn className="py-3 px-4 bg-neutral-100 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider min-w-[120px] border-b border-neutral-200">Vervaldatum</TableColumn>
                        <TableColumn className="py-3 px-4 bg-neutral-100 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider min-w-[200px] border-b border-neutral-200">Relaties</TableColumn>
                        <TableColumn className="py-3 px-4 bg-neutral-100 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider border-b border-neutral-200">Acties</TableColumn>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow className="border-b border-neutral-200 group">
                                <TableCell className="sticky left-0 z-10 py-4 px-4 text-center text-neutral-500 bg-white border-r border-neutral-200">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">Laden...</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                            </TableRow>
                        ) : sortedFileLogs.length === 0 ? (
                            <TableRow className="border-b border-neutral-200 group">
                                <TableCell className="sticky left-0 z-10 py-4 px-4 text-center text-neutral-500 bg-white border-r border-neutral-200">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">Geen documenten gevonden</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                                <TableCell className="py-4 px-4 text-center text-neutral-500 bg-white">-</TableCell>
                            </TableRow>
                        ) : (
                            (sortedFileLogs || []).map((log: any, index: number) => (
                                <TableRow key={index} className="border-b border-neutral-200 group hover:bg-neutral-50">
                                    <TableCell className="sticky left-0 z-10 py-3 px-4 align-middle whitespace-nowrap bg-white group-hover:bg-neutral-50 border-r border-neutral-200">
                                        {log.unit ? log.unit?.id : log.building?.id ? log.building?.id + " gemeenschap" : "ID niet gevonden"}
                                    </TableCell>
                                    <TableCell className="py-3 px-4 align-middle bg-white group-hover:bg-neutral-50">{log.label?.join(", ") || "-"}</TableCell>
                                    <TableCell className="py-3 px-4 align-middle bg-white group-hover:bg-neutral-50">
                                        {editMode[log.id] ? (
                                            <Select
                                                selectedKeys={editedLogs[log.id]?.status ? [editedLogs[log.id].status] : [log.status]}
                                                onSelectionChange={(keys) => {
                                                    const selectedStatus = Array.from(keys)[0] as FileLogStatus;
                                                    handleStatusChange(log.id, { target: { value: selectedStatus } } as React.ChangeEvent<HTMLSelectElement>);
                                                }}
                                                className="min-w-[200px]"
                                            >
                                                {statusOptions.map(option => (
                                                    <SelectItem
                                                        key={option.value}
                                                        className="flex items-center gap-2"
                                                        startContent={<div className={`h-3 w-3 rounded-full ${option.color}`} />}
                                                    >
                                                        {option.label}
                                                    </SelectItem>
                                                ))}
                                            </Select>
                                        ) : (
                                            <div onClick={() => toggleEditMode(log.id)} className="flex items-center gap-2 cursor-pointer min-h-[40px] p-2 -m-2 rounded-sm hover:bg-neutral-100">
                                                <span className={`h-3 w-3 rounded-full shrink-0 ${statusOptions.find(opt => opt.value === log.status)?.color}`} />
                                                {statusOptions.find(opt => opt.value === log.status)?.label}
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="py-3 px-4 align-middle min-w-[300px] bg-white group-hover:bg-neutral-50">
                                        {editMode[log.id] ? (
                                            <Textarea
                                                value={editedLogs[log.id]?.statusDescription ?? log.statusDescription ?? ''}
                                                onChange={(e) => handleDescriptionChange(log.id, e.target.value)}
                                                placeholder="Voeg opmerkingen op de conformiteit toe..."
                                                className="w-full"
                                                minRows={3}
                                                variant="bordered"
                                            />
                                        ) : (
                                            <div onClick={() => toggleEditMode(log.id)} className="cursor-pointer min-h-[40px] p-2 -m-2 rounded-sm hover:bg-neutral-100 break-words whitespace-normal">
                                                {log.statusDescription || '-'}
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="py-3 px-4 align-middle min-w-[300px] bg-white group-hover:bg-neutral-50">
                                        {editMode[log.id] ? (
                                            <Textarea
                                                value={editedLogs[log.id]?.summary ?? log.summary ?? ''}
                                                onChange={(e) => handleSummaryChange(log.id, e.target.value)}
                                                placeholder="Voeg samenvatting toe..."
                                                className="w-full"
                                                minRows={3}
                                                variant="bordered"
                                            />
                                        ) : (
                                            <div onClick={() => toggleEditMode(log.id)} className="cursor-pointer min-h-[40px] p-2 -m-2 rounded-sm hover:bg-neutral-100 break-words whitespace-normal">{log.summary || "-"}</div>
                                        )}
                                    </TableCell>
                                    <TableCell className="py-3 px-4 align-middle whitespace-nowrap bg-white group-hover:bg-neutral-50">
                                        {editMode[log.id] ? (
                                            <Input
                                                type="date"
                                                value={editedLogs[log.id]?.documentDate ?? log.documentDate?.split('T')[0] ?? ''}
                                                onChange={(e) => handleDateChange(log.id, 'documentDate', e.target.value)}
                                                className="w-full"
                                                size="sm"
                                            />
                                        ) : (
                                            <div onClick={() => toggleEditMode(log.id)} className="cursor-pointer min-h-[40px] p-2 -m-2 rounded-sm hover:bg-neutral-100">
                                                {log.documentDate ? new Date(log.documentDate).toLocaleDateString() : "-"}
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="py-3 px-4 align-middle whitespace-nowrap bg-white group-hover:bg-neutral-50">
                                        {editMode[log.id] ? (
                                            <Input
                                                type="date"
                                                value={editedLogs[log.id]?.expirationDate ?? log.expirationDate?.split('T')[0] ?? ''}
                                                onChange={(e) => handleDateChange(log.id, 'expirationDate', e.target.value)}
                                                className="w-full"
                                                size="sm"
                                            />
                                        ) : (
                                            <div onClick={() => toggleEditMode(log.id)} className="cursor-pointer min-h-[40px] p-2 -m-2 rounded-sm hover:bg-neutral-100">
                                                {log.expirationDate ? new Date(log.expirationDate).toLocaleDateString() : "-"}
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="py-3 px-4 align-middle min-w-[250px] bg-white group-hover:bg-neutral-50">
                                        {editMode[log.id] ? (
                                            <div className="flex flex-col gap-2">
                                                <SearchComponent
                                                    label="Koppel de relatie aan dit document (maker, begunstigde, ...)"
                                                    resourceFilter={[ResourceType.COMPANY, ResourceType.CONTACT]}
                                                    onSelect={(relation) => handleRelationSelect(log.id, relation)}
                                                    mode={SearchMode.SEARCH_AND_PRESENT}
                                                    editMode={true}
                                                />
                                                <div className="flex flex-wrap gap-2 mt-1">
                                                    {(editedLogs[log.id]?.relations || log.relations || []).map((relation: any) => (
                                                        <Chip
                                                            key={relation.resourceId}
                                                            onClose={() => handleRemoveRelation(log.id, relation.resourceId)}
                                                            variant="flat"
                                                            className="bg-neutral-100"
                                                        >
                                                            {relation.name}
                                                        </Chip>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <div onClick={() => toggleEditMode(log.id)} className="cursor-pointer min-h-[40px] p-2 -m-2 rounded-sm hover:bg-neutral-100">
                                                <div className="flex flex-wrap gap-2">
                                                    {(log.relations || []).map((relation: any) => (
                                                        <Chip
                                                            key={relation.resourceId}
                                                            className="gap-1"
                                                            startContent={<User weight="duotone" size={18} />}
                                                            classNames={{
                                                                base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                                                                content: "px-0 font-medium text-clip overflow-hidden flex items-center",
                                                            }}
                                                        >
                                                            <Link to={`/${relation.type}/${relation.resourceId}`} className="text-moss-primary hover:underline">
                                                                {relation.name}
                                                            </Link>
                                                            <ArrowUpRight className="h-4 w-4 ml-1 shrink-0" />
                                                        </Chip>
                                                    ))}
                                                    <div className="text-neutral-500 italic">
                                                        {!log.relations || log.relations.length === 0 && "Geen relaties gekoppeld"}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="py-3 px-4 align-middle whitespace-nowrap bg-white group-hover:bg-neutral-50">
                                        <div className="flex gap-3 items-center">
                                            {log.sharePointFileUrl && (
                                                <Tooltip content="Bekijk document">
                                                    <a
                                                        href={log.sharePointFileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center text-neutral-600 hover:text-moss-primary"
                                                    >
                                                        <Eye className="h-5 w-5 cursor-pointer" />
                                                    </a>
                                                </Tooltip>
                                            )}
                                            <Tooltip content="Verwijder document">
                                                <button
                                                    onClick={() => {
                                                        setFileToDelete(log);
                                                        onOpen();
                                                    }}
                                                    className="flex items-center text-red-500 hover:text-red-700"
                                                >
                                                    <Trash className="h-5 w-5 cursor-pointer" />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <Modal
                isOpen={isOpen}
                onOpenChange={onClose}
                isDismissable={true}
                isKeyboardDismissDisabled={true}
                className="font-normal text-neutral-primary"
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">
                                <p className="flex items-center gap-1 text-sm font-medium text-status-bad">
                                    <AlertCircle className="h-[17px] w-[17px]" /> Verwijder bestand
                                </p>
                            </ModalHeader>
                            <ModalBody>
                                <p>
                                    Wil je deze bestand verwijderen uit de Yallyhub? Zo ja, dan
                                    wordt deze ook van SharePoint verwijderd.
                                </p>
                            </ModalBody>
                            <ModalFooter className="justify-start">
                                <ButtonNew
                                    variant="primaryStatusRed"
                                    onClick={handleDelete}
                                    icon={<Trash />}
                                    label="Verwijder bestand"
                                />
                                <ButtonNew
                                    variant="secondaryNeutral"
                                    onClick={onClose}
                                    label="Nee, toch niet"
                                />
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </div>
    );
});

export default FileLogTable;